import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../api/auth';
import { toast } from '../components/ui/Toast';
import { AxiosError } from 'axios';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');
  const { setAuth } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validate = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email.trim()) newErrors.email = 'E-Mail ist erforderlich';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Ungültige E-Mail-Adresse';
    if (!password) newErrors.password = 'Passwort ist erforderlich';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await authApi.login({ email, password });
      // Set tokens first so getProfile() has the auth header
      useAuthStore.getState().setTokens(response.access_token, response.refresh_token);
      const user = await authApi.getProfile();
      setAuth(user, response.access_token, response.refresh_token);
      toast.success('Willkommen!', `Hallo ${user.name}`);
      navigate(redirectTo || '/');
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      if (error.response?.status === 401) {
        toast.error('Anmeldung fehlgeschlagen', 'E-Mail oder Passwort falsch.');
      } else {
        toast.error('Fehler', 'Ein unerwarteter Fehler ist aufgetreten.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left: Decorative Panel (Desktop) */}
      <div className="hidden lg:flex lg:w-1/2 gradient-primary relative overflow-hidden flex-col items-center justify-center p-12">
        {/* Decorative shapes */}
        <div className="absolute top-20 left-20 w-40 h-40 bg-white/10 rounded-full blur-xl" />
        <div className="absolute bottom-32 right-16 w-56 h-56 bg-white/5 rounded-full blur-2xl" />
        <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-white/10 rounded-2xl rotate-12" />

        <div className="relative z-10 text-center max-w-md">
          <img src="/logo.png" alt="PaarKurs" className="w-24 h-24 mx-auto mb-6 animate-float" />
          <h1 className="text-4xl font-bold text-white mb-3">PaarKurs</h1>
          <p className="text-xl text-white/80 font-medium">Deine Ausgaben im Griff.</p>
          <p className="text-white/50 mt-4 text-sm leading-relaxed">
            Scanne deine Kassenzettel, behalte den Überblick und spare smarter.
          </p>

          <div className="flex justify-center gap-4 mt-10">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 text-center">
              <span className="text-2xl block">{'\uD83D\uDCF7'}</span>
              <span className="text-xs text-white/70 mt-1 block">Scannen</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 text-center">
              <span className="text-2xl block">{'\uD83D\uDCCA'}</span>
              <span className="text-xs text-white/70 mt-1 block">Analysieren</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3 text-center">
              <span className="text-2xl block">{'\uD83D\uDCB0'}</span>
              <span className="text-xs text-white/70 mt-1 block">Sparen</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right: Login Form */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-[#0f0f1a] dark:via-[#0f0f1a] dark:to-[#131325] px-4 py-8">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 gradient-primary rounded-2xl shadow-glow-primary mb-4">
              <span className="text-3xl">{'\uD83E\uDDFE'}</span>
            </div>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-neutral-800 dark:text-white">
              Willkommen zurück! {'\uD83C\uDF89'}
            </h1>
            <p className="text-neutral-400 dark:text-neutral-500 mt-2">Melde dich bei deinem Konto an</p>
          </div>

          {/* Form Card */}
          <div className="bg-white/60 dark:bg-[#1e1e2e]/60 backdrop-blur-xl rounded-3xl shadow-lg border border-white/50 dark:border-indigo-500/10 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <Input
                label="E-Mail"
                type="email"
                placeholder="deine@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
                icon={<Mail className="w-4 h-4" />}
                autoComplete="email"
                autoFocus
              />

              <Input
                label="Passwort"
                type="password"
                placeholder="Dein Passwort"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                icon={<Lock className="w-4 h-4" />}
                autoComplete="current-password"
              />

              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={loading}
              >
                Anmelden {'\uD83D\uDE80'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-xs text-neutral-400 dark:text-neutral-500">
                Private App — nur für eingeladene Nutzer
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
